import {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Row} from 'antd'
import {getRequest, postRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'

const HarnessRelationForm = ({ harnessId, onSuccess, onClose }) => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [salesCodeOptions, setSalesCodeOptions] = useState([])
  const [partNumberOptions, setPartNumberOptions] = useState([])
  const [harnessTypeOptions, setHarnessTypeOptions] = useState([])
  const [saving, setSaving] = useState(false)

  const fetchOptions = useCallback(async () => {
    const [scRes, pnRes, htRes] = await Promise.all([
      getRequest(`/api/option_codes/options/?vehicle=${vehicle}&search=`),
      getRequest(`/api/part_number/?vehicle=${vehicle}&search=`),
      getRequest(`/api/harness_type/?vehicle=${vehicle}&search=`),
    ])
    setSalesCodeOptions(
      (scRes?.data || []).map((o) => ({ value: o.value, label: o.label }))
    )
    setPartNumberOptions(
      (pnRes?.results || []).map((o) => ({ value: o.id, label: o.name }))
    )
    setHarnessTypeOptions(
      (htRes?.results || []).map((o) => ({ value: o.id, label: o.name }))
    )
  }, [vehicle])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  const handleSave = async (values) => {
    setSaving(true)

    const extractId = (v) =>
      v && typeof v === 'object' ? v.value : v

    const formData = new FormData()
    formData.append('vehicle', vehicle)
    formData.append('sales_code', extractId(values.sales_code) || '')
    formData.append('harness_type', extractId(values.harness_type) || '')
    if (Array.isArray(values.part_number)) {
      values.part_number.forEach((pn) => {
        formData.append('part_number', extractId(pn) || '')
      })
    }

    let res = await postRequest(
      `/api/harnesses/related_data/save/${harnessId}/`,
      formData
    )

    setSaving(false)

    if (res?.status) {
      if (res?.error_response?.length) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: Array.isArray(res.error_response)
              ? res.error_response.join(' | ')
              : res.error_response,
          },
        })
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'harnesses.mappingSaveSuccess' }),
          },
        })
        form.resetFields()
        onSuccess?.()
      }
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'harnesses.mappingSaveError' }),
        },
      })
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      style={{ width: '100%' }}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <SelectInput
            name="sales_code"
            label={intl.formatMessage({ id: 'harnesses.salesCode' })}
            options={salesCodeOptions}
            isRequired
            placeholder={intl.formatMessage({ id: 'harnesses.selectSalesCode' })}
            errorHelp={false}
            errorMsg={intl.formatMessage({ id: 'harnesses.salesCodeRequired' })}
          />
        </Col>

        <Col span={12}>
          <SelectInput
            name="harness_type"
            label={intl.formatMessage({ id: 'harnesses.harnessType' })}
            options={harnessTypeOptions}
            isRequired
            placeholder={intl.formatMessage({ id: 'harnesses.selectHarnessType' })}
            errorHelp={false}
            errorMsg={intl.formatMessage({ id: 'harnesses.harnessTypeRequired' })}
          />
        </Col>

        <Col span={24}>
          <SelectInput
            name="part_number"
            label={intl.formatMessage({ id: 'harnesses.partNumber' })}
            options={partNumberOptions}
            isMulti
            isRequired
            placeholder={intl.formatMessage({ id: 'harnesses.selectPartNumber' })}
            errorHelp={false}
            errorMsg={intl.formatMessage({ id: 'harnesses.partNumberRequired' })}
            maxTagCount={3}
          />
        </Col>

        <Col span={24}>
          <Flex justify="end" gap={16}>
            <Button onClick={onClose}>
              {intl.formatMessage({ id: 'forms.cancel' })}
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {intl.formatMessage({ id: 'harnesses.saveMapping' })}
            </Button>
          </Flex>
        </Col>
      </Row>
    </Form>
  )
}

export default HarnessRelationForm
